import React, { useState, useRef } from "react";
import {
  Document, Packer, Paragraph, TextRun, AlignmentType,
  PageBreak, Table, TableRow, TableCell, WidthType, BorderStyle,
} from "docx";
import { saveAs } from "file-saver";

/**
 * Federal Tax Post-Issuance Compliance Manual Builder
 * --------------------------------------------------------------------------
 * Standalone module for PublicFinanceTax.com.
 *
 * Workflow:
 *   1. Attorney fills out an intake form for a single 501(c)(3) borrower
 *      whose qualified 501(c)(3) bonds finance acquired residential rental
 *      property and therefore rely on the section 145(d)(2)(B) exception
 *      (qualified residential rental project as defined in section 142(d)).
 *   2. The module assembles the manual (cover page, significance statement,
 *      responsible-party block, table of contents, and the substantive
 *      sections) and downloads it as an editable Word (.docx) document that
 *      the attorney delivers to the client. Clients do not access the program.
 *
 * No data is pulled from other parts of the program. Everything is entered
 * on the intake form. State is in-memory only (no browser storage).
 */

// ---- helpers ----------------------------------------------------------
const fmtDate = (d) => {
  if (!d) return "____________";
  const dt = new Date(d + "T00:00:00");
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

// Format a user-entered amount as US currency. Accepts raw digits, or input
// that already has $ and commas; returns "$1,234,567" (or "$1,234,567.89" if
// cents were entered). Returns the original string if it cannot be parsed.
const fmtCurrency = (val) => {
  if (val == null) return "";
  const s = String(val).trim();
  if (!s) return "";
  const cleaned = s.replace(/[^0-9.]/g, "");
  if (!cleaned) return s;
  const num = Number(cleaned);
  if (isNaN(num)) return s;
  const hasCents = cleaned.includes(".") && cleaned.split(".")[1].length > 0;
  return num.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  });
};

const para = (text, opts = {}) =>
  new Paragraph({
    spacing: { after: 160, line: 276 },
    alignment: opts.align || AlignmentType.JUSTIFIED,
    children: runs(text),
    ...opts.p,
  });

// Allow simple **bold** segments inside body text.
// Convert straight quotes and apostrophes to typographic (curly) ones.
// Order matters: handle apostrophes/right-singles, then open/close doubles.
const smartQuotes = (text) => {
  let s = String(text);
  // Opening double quote: start of string or after whitespace/opening bracket.
  s = s.replace(/(^|[\s([{<])"/g, "$1\u201C");
  // Any remaining double quotes are closing.
  s = s.replace(/"/g, "\u201D");
  // Apostrophe between letters (contractions/possessives) -> right single.
  s = s.replace(/([A-Za-z])'([A-Za-z])/g, "$1\u2019$2");
  // Opening single quote: start or after whitespace/opening bracket.
  s = s.replace(/(^|[\s([{<])'/g, "$1\u2018");
  // Any remaining single quotes are closing/apostrophes.
  s = s.replace(/'/g, "\u2019");
  return s;
};

function runs(text) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return parts.map((seg) => {
    if (seg.startsWith("**") && seg.endsWith("**")) {
      return new TextRun({ text: smartQuotes(seg.slice(2, -2)), bold: true });
    }
    return new TextRun({ text: smartQuotes(seg) });
  });
}

const bullet = (text) =>
  new Paragraph({ text: undefined, bullet: { level: 0 }, spacing: { after: 80, line: 276 }, children: runs(text) });

const sectionHeading = (label, title) => [
  new Paragraph({
    pageBreakBefore: true,
    spacing: { before: 0, after: 140 },
    keepNext: true,
    keepLines: true,
    children: [
      new TextRun({ text: label, bold: true, size: 20, color: "1F4E6B" }),
    ],
  }),
  new Paragraph({
    spacing: { after: 200 },
    keepNext: true,
    keepLines: true,
    border: { bottom: { style: BorderStyle.SINGLE, size: 12, color: "1F4E6B", space: 6 } },
    children: [
      new TextRun({ text: smartQuotes(title), bold: true, size: 32 }),
    ],
  }),
];

// Locks a section heading to its first body paragraph by placing both inside a
// single table row marked cantSplit. A cantSplit row cannot break across a
// page, so Word and LibreOffice both keep the heading with its opening text.
// The table is borderless so it reads as ordinary text. pageBreakBefore on the
// row's first paragraph starts each section on a fresh page.
function lockedSectionStart(label, title, firstBodyNode, opts = {}) {
  const noBorder = { style: BorderStyle.NONE, size: 0, color: "FFFFFF" };
  const headingParas = [
    new Paragraph({
      pageBreakBefore: opts.pageBreakBefore !== false,
      spacing: { before: 0, after: 140 },
      children: [new TextRun({ text: label, bold: true, size: 20, color: "1F4E6B" })],
    }),
    new Paragraph({
      spacing: { after: 200 },
      border: { bottom: { style: BorderStyle.SINGLE, size: 12, color: "1F4E6B", space: 6 } },
      children: [new TextRun({ text: smartQuotes(title), bold: true, size: 32 })],
    }),
  ];
  const cellChildren = firstBodyNode ? [...headingParas, firstBodyNode] : headingParas;
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: noBorder, bottom: noBorder, left: noBorder, right: noBorder,
      insideVertical: noBorder, insideHorizontal: noBorder,
    },
    rows: [
      new TableRow({
        cantSplit: true,
        children: [
          new TableCell({
            width: { size: 100, type: WidthType.PERCENTAGE },
            margins: { top: 0, bottom: 0, left: 0, right: 0 },
            children: cellChildren,
          }),
        ],
      }),
    ],
  });
}

function infoTable(rows) {
  const noBorder = { style: BorderStyle.NONE, size: 0, color: "FFFFFF" };
  const bottom = { style: BorderStyle.SINGLE, size: 4, color: "D4D9E0" };
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: { top: noBorder, left: noBorder, right: noBorder, insideVertical: noBorder, insideHorizontal: bottom, bottom: noBorder },
    rows: rows.map(([k, v]) =>
      new TableRow({
        children: [
          new TableCell({
            width: { size: 38, type: WidthType.PERCENTAGE },
            children: [new Paragraph({ spacing: { after: 60 }, children: [new TextRun({ text: k, bold: true, color: "5A6473", size: 21 })] })],
          }),
          new TableCell({
            width: { size: 62, type: WidthType.PERCENTAGE },
            children: [new Paragraph({ spacing: { after: 60 }, children: [new TextRun({ text: v || "\u2014", size: 21 })] })],
          }),
        ],
      })
    ),
  });
}

// A blank grid for recording periodic reviews over several years.
function reviewTable(numRows = 5) {
  const line = { style: BorderStyle.SINGLE, size: 4, color: "B9C0CB" };
  const headerCell = (text, width) =>
    new TableCell({
      width: { size: width, type: WidthType.PERCENTAGE },
      shading: { fill: "EEF1F5" },
      margins: { top: 60, bottom: 60, left: 100, right: 100 },
      children: [new Paragraph({ children: [new TextRun({ text, bold: true, size: 20, color: "1F4E6B" })] })],
    });
  const bodyCell = (width) =>
    new TableCell({
      width: { size: width, type: WidthType.PERCENTAGE },
      margins: { top: 120, bottom: 120, left: 100, right: 100 },
      children: [new Paragraph({ children: [new TextRun({ text: "", size: 21 })] })],
    });
  const header = new TableRow({
    tableHeader: true,
    children: [headerCell("Reviewed by", 40), headerCell("Title", 35), headerCell("Date of review", 25)],
  });
  const blanks = Array.from({ length: numRows }, () =>
    new TableRow({ children: [bodyCell(40), bodyCell(35), bodyCell(25)] })
  );
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: line, bottom: line, left: line, right: line,
      insideVertical: line, insideHorizontal: line,
    },
    rows: [header, ...blanks],
  });
}

// ---- significance copy (mirrors significanceParagraphs in the component) --
function significanceParagraphs(borrowerName) {
  const name = borrowerName || "[Borrower Name]";
  return [
    `${name} (the "Borrower") has borrowed proceeds of the tax-exempt bonds identified in Section 2 of this Manual (the "Bonds") to acquire, own, and operate the residential rental projects identified in Section 2 (each, a "Project").`,
    `The Bonds were issued as qualified 501(c)(3) bonds under Section 145 of the Internal Revenue Code of 1986, as amended (the "Code"). Because all or a portion of the net proceeds of the Bonds were used to provide residential rental property for family units that was not newly constructed, the Bonds rely upon the exception in Section 145(d)(2)(B) for qualified residential rental projects described in Section 142(d) of the Code.`,
    `Accordingly, each Project must satisfy the applicable requirements of Section 142(d) throughout its qualified project period, including the minimum low-income occupancy set-aside elected by the Issuer at the time of issuance. The applicable election, Project-specific income limits, qualified project period, and other Project-specific requirements are identified in the Project Compliance Profile maintained in this Manual.`,
    `The requirements of Section 142(d) are separate from, and are not replaced by, any requirements imposed under Section 42, a regulatory agreement, a loan agreement, a housing-assistance-payment contract, or state or local affordable-housing law. Compliance documentation prepared for another program may be used as supporting evidence, but does not by itself establish compliance with the federal tax requirements applicable to the Bonds.`,
    `Failure to satisfy the applicable Section 142(d) requirements on a continuing basis may cause the related Project to cease to qualify as a qualified residential rental project and may jeopardize the federal tax-exempt status of interest on the Bonds. A failure to file Form 8703 is separately subject to statutory penalties and must be corrected promptly, although the filing failure alone does not determine the tax status of the Bonds.`,
    `These policies are intended to establish an operating system for identifying, documenting, and escalating federal tax compliance matters. They do not replace the Tax Certificate, Loan Agreement, Regulatory Agreement, Indenture, bond documents, or applicable law. If this Manual conflicts with any controlling bond document, the controlling bond document governs. No person may waive or modify a federal tax covenant without the written approval of the Borrower, the Issuer when required, and bond counsel.`,
  ];
}

// Section body builders keyed by id. Each returns an array of Paragraphs/Tables.
function sectionBody(id, form) {
  const bonds = form.bonds || [];
  switch (id) {
    case "responsibility":
      return [
        para("The Borrower designates the officer identified below as its Federal Tax Compliance Officer. The Federal Tax Compliance Officer is responsible for administering these policies, maintaining the compliance file, coordinating with the Project operator and property manager, and causing material matters to be referred to bond counsel."),
        infoTable([
          ["Federal Tax Compliance Officer", form.ftcoName || "____________________"],
          ["Title", form.ftcoTitle || "____________________"],
          ["Email / Telephone", form.ftcoContact || "____________________"],
        ]),
        para("The Federal Tax Compliance Officer may delegate particular administrative tasks to employees, affiliates, property managers, accountants, rebate consultants, or other service providers. Any delegation must be documented in writing and must identify the delegated responsibilities, reporting deadlines, and required records. Delegation does not relieve the Borrower of responsibility for compliance with its bond documents or applicable federal tax requirements."),
        para("The person identified as the operator of a Project for Form 8703 purposes must be the person to whom the Issuer or owner has delegated responsibility for ensuring compliance with the qualified residential rental project requirements. The identity of the operator, the person authorized to sign Form 8703, and the identity of the property manager must be shown in the Project Compliance Profile."),
        para("The Federal Tax Compliance Officer shall conduct, or cause to be conducted, an annual compliance review for each Project. The annual review must include:"),
        bullet("review of the Project's Section 142(d) unit counts, tenant-income certifications, available-unit information, vacancy information, and any applicable next-available-unit analysis;"),
        bullet("confirmation that the Project remains residential rental property available to the general public on a non-transient basis;"),
        bullet("review of new or renewed management agreements, service contracts, leases, master leases, commercial-use arrangements, and other special legal entitlements affecting the Project;"),
        bullet("confirmation of continued ownership by a governmental unit or a Section 501(c)(3) organization;"),
        bullet("review of any sale, transfer, refinancing, casualty, condemnation, insurance recovery, substantial rehabilitation, or change in Project operations;"),
        bullet("preparation, review, execution, filing, and retention of Form 8703 and evidence of timely filing; and"),
        bullet("review of arbitrage, rebate, investment, and record-retention obligations under the applicable Tax Certificate."),
        para("The property manager shall deliver an annual written Section 142(d) compliance report to the Federal Tax Compliance Officer no later than January 31 of each year. The report must provide sufficient information to complete and support Form 8703 for the preceding calendar year and must identify any actual or potential failure, including over-income tenants, available comparable or smaller units leased to nonqualifying tenants, vacancies, units held offline for renovation, short-term occupancy, master leases, and deviations from ordinary tenant-screening procedures."),
        para("The Federal Tax Compliance Officer shall cause Form 8703 to be reviewed and filed sufficiently in advance of the March 31 filing deadline and shall retain a completed copy of the filed form, proof of filing, and all supporting records."),
        para("Before entering into, approving, or materially modifying any of the following, the Federal Tax Compliance Officer shall consult bond counsel:"),
        bullet("sale, transfer, merger, change of control, or other disposition of any Project or material portion thereof;"),
        bullet("change in ownership, organizational form, or federal tax-exempt status of the Borrower or Project owner;"),
        bullet("conversion to condominium, cooperative, transient, hotel, student-housing, assisted-living, or other nontraditional residential use;"),
        bullet("any new property-management agreement, master lease, corporate-housing arrangement, commercial lease, concession, rooftop or telecommunications arrangement, solar arrangement, laundry arrangement, advertising arrangement, or revenue-sharing arrangement;"),
        bullet("any restriction or preference affecting tenant eligibility, including a veterans, employer, university, social-organization, or supportive-housing restriction;"),
        bullet("any material change in Section 8 or other housing-assistance arrangements;"),
        bullet("casualty, condemnation, insurance proceeds, sale proceeds, refinancing, defeasance, redemption, or modification of the Bonds or loan documents; or"),
        bullet("discovery of any potential violation of Section 142(d), Section 145, Section 148, or the Tax Certificate."),
        para("Any employee, property manager, or other service provider that identifies a potential compliance failure shall notify the Federal Tax Compliance Officer promptly, and in no event later than five business days after discovery. The Borrower shall preserve all relevant records, take reasonable steps to prevent additional noncompliance, and consult bond counsel before attempting corrective action."),
      ];
    case "bonds": {
      const out = [para("The following qualified 501(c)(3) bond issues are outstanding:")];
      bonds.forEach((b, i) => {
        out.push(new Paragraph({ spacing: { before: 120, after: 60 }, children: [new TextRun({ text: b.issueName || `Bond Issue ${i + 1}`, bold: true, size: 23 })] }));
        out.push(infoTable([
          ["Issuer", b.issuer || "\u2014"],
          ["Date of Issue", b.issueDate ? fmtDate(b.issueDate) : "\u2014"],
          ["Principal Amount", b.principalAmount ? fmtCurrency(b.principalAmount) : "\u2014"],
          ["Final Maturity", b.finalMaturity ? fmtDate(b.finalMaturity) : "\u2014"],
          ["Financed Project", b.projectName || "\u2014"],
          ["Location", b.projectLocation || "\u2014"],
          ["Residential Units", b.units || "\u2014"],
        ]));
      });
      return out;
    }
    case "certs": {
      const out = [
        para("The executed tax certificate for each bond issue is attached to this manual. Each tax certificate sets forth the Borrower's representations and covenants concerning the federal tax requirements applicable to the related bonds, including the use of proceeds, the use of the financed facilities, arbitrage and rebate, and the residential rental set-aside."),
        para("[Attach the executed tax certificate for each issue behind this page.]"),
      ];
      bonds.forEach((b, i) => out.push(bullet(`${b.issueName || `Bond Issue ${i + 1}`}${b.issueDate ? ` (${fmtDate(b.issueDate)})` : ""}`)));
      return out;
    }
    case "setaside-explanation": {
      const out = [
        para('Because all or a portion of the net proceeds of the Bonds were used to provide residential rental property for family units that was not newly constructed, the Bonds rely upon the exception in Section 145(d)(2)(B) of the Code for qualified residential rental projects described in Section 142(d) of the Code. Each Project must therefore satisfy a continuing low-income occupancy set-aside throughout its qualified project period.'),
        para("**The set-aside.** The Issuer elected, at the time of issuance, one of the following set-aside requirements for each Project. This Manual does not amend the election; the applicable election for each Project is identified in the Project Compliance Profile."),
        bullet("at least 20% of the residential units occupied by individuals whose income is 50% or less of area median gross income (the \"20-50 test\"); or"),
        bullet("at least 40% of the residential units occupied by individuals whose income is 60% or less of area median gross income (the \"40-60 test\")."),
        para("**Qualified project period.** For an acquired, already-operating Project in which at least 10% of the units were occupied before the issue date, the qualified project period generally begins on the issue date. The set-aside requirements apply to the total number of available units beginning on the later of the first day on which at least 10% of all residential units are occupied and the issue date. The qualified project period ends on the latest of (i) the date that is 15 years after the date on which at least 50% of the residential units were first occupied, (ii) the first day on which no tax-exempt bond issued with respect to the Project is outstanding, or (iii) the date on which any assistance provided with respect to the Project under Section 8 of the United States Housing Act of 1937 terminates."),
        para("**Available-unit rule.** If the income of an occupant of a low-income unit increases above the applicable limit, the project may continue to treat that unit as a low-income unit only so long as the next available unit of comparable or smaller size in the project is rented to a qualifying tenant. The Federal Tax Compliance Officer should confirm that the property manager applies this rule on a continuing basis."),
        para("**Annual certification.** The operator of each Project must certify annually to the Secretary, on IRS Form 8703, that the Project met the applicable set-aside requirement. Revenue Procedure 2004-39 sets forth the procedures for determining whether a Project satisfies the Section 142(d) set-aside requirements during the qualified project period. The certification is described in the following section."),
        para("**Transition period for acquired projects.** Under Revenue Procedure 2004-39, a special rule applies to the acquisition of an existing residential rental project. For a period of 12 months beginning on the issue date of the first bonds (the \"transition period\"), a failure to satisfy the set-aside requirement will not cause the Project to fail to be a qualified residential rental project. This special rule does not apply if more than 90% of the residential units in the Project are not available units at any time within the 60-day period after the later of the date the Project is acquired or the issue date of the first bonds. Whether the transition period was used for a Project, and the date on which the Project first satisfied the elected set-aside, are documented in the Project Compliance Profile."),
      ];
      bonds.filter((b) => b.detailLevel === "detailed").forEach((b, i) => {
        out.push(new Paragraph({ spacing: { before: 160, after: 80 }, children: [new TextRun({ text: `Additional Requirements: ${b.projectName || b.issueName || `Project ${i + 1}`}`, bold: true, size: 23 })] }));
        out.push(para("The set-aside election was made by the Issuer at the time of issuance and generally may not be revoked. Income limits are adjusted for family size, and the income of all occupants of a unit is counted. The Section 142(d) set-aside is an income test, not a rent test (unlike the low-income housing tax credit under Section 42). Where the Project also carries Section 42 credits, care should be taken not to conflate the two regimes, because the income limits, rent restrictions, and compliance periods differ."));
        out.push(para("**Deep-rent-skewed alternative.** The Project may qualify under the deep-rent-skewed alternative of Section 142(d)(4)(B), under which 15% or more of the low-income units are occupied by tenants whose income is 40% or less of area median gross income and the gross rent on each low-income unit does not exceed one-half the gross rent on comparable market-rate units. A Project relying on this alternative is subject to additional rent and occupancy constraints and should be flagged separately in the annual review."));
        out.push(para("**Income recertification.** Tenant income is determined and certified at the commencement of occupancy. Under Section 142(d)(3), an owner need not annually recertify tenant income for a year in which no residential unit in the Project is occupied by a new resident whose income exceeds the applicable income limit. The operator should retain the supporting income documentation for each certification. The relevant authorities are Section 142(d)(3), Treasury Regulations Section 1.103-8(b), and Revenue Procedure 2004-39."));
      });
      return out;
    }
    case "form8703-explanation":
      return [
        para("IRS Form 8703, Annual Certification of a Residential Rental Project, is the form on which the operator of a qualified residential rental project certifies, for each calendar year in the qualified project period, that the project complied with the applicable low-income set-aside."),
        para("**Who must file.** The operator of the Project must file a separate Form 8703 for each Project. For this purpose, the operator is the person to whom the Issuer or owner has delegated responsibility for ensuring compliance with the qualified residential rental project requirements. A property manager is not automatically the operator merely because it manages the building. The operator for each Project is identified in the Project Compliance Profile."),
        para("**When to file.** Form 8703 must be filed annually, no later than March 31 following the close of each calendar year in the qualified project period. A Form 8703 may remain due after the Bonds are redeemed if the qualified project period has not ended."),
        para("**What it requires.** The form identifies the Project and the issue that financed it, states the set-aside election in effect, and reports the number of units and the number occupied by qualifying low-income tenants as of the certification date."),
        para("**Effect of a filing failure.** A failure to file Form 8703 does not, by itself, affect the tax-exempt status of interest on the Bonds, but it can trigger the penalty under Section 6652(j) and must be corrected promptly."),
      ];
    case "form8703-copies": {
      const out = [
        para("A copy of the most recently filed Form 8703 for each financed project is attached behind this page and retained as part of the compliance record."),
        para("[Attach the filed Form 8703 for each project listed below.]"),
      ];
      bonds.forEach((b, i) => out.push(bullet(`${b.projectName || `Project ${i + 1}`}${b.projectLocation ? ` \u2014 ${b.projectLocation}` : ""}${b.setAsideElection ? ` (${b.setAsideElection} election)` : ""}`)));
      return out;
    }
    case "use-restrictions": {
      const out = [
        para("For the life of the Bonds, each Project must continuously satisfy the operational requirements of a qualified residential rental project:"),
        bullet("The Project must be residential rental property used on a continuous, non-transient basis (subject to the limited exceptions for certain single-room-occupancy and transitional housing)."),
        bullet("All residential units must be available for use by the general public and may not be used on a transient basis."),
        bullet("The Project must remain a discrete edifice or set of facilities containing similarly constructed units."),
        bullet("The applicable low-income set-aside must be maintained throughout the qualified project period, including compliance with the available-unit rule."),
        para("A Project does not fail to be residential rental property merely because part of the building or structure is used for purposes other than residential rental. However, mixed-use and commercial space, leasing of common areas, short-term occupancy, corporate housing, master leases, and similar arrangements can affect qualification and must be pre-cleared with bond counsel as provided in Section 1 before they are entered into or modified."),
      ];
      if (bonds.some((b) => b.detailLevel === "detailed")) {
        out.push(para("**Continuous-rental and facilities requirements.** Under Treasury Regulations Section 1.103-8(b) and Section 142(d), the Project must consist of a building or structure containing similarly constructed units, together with functionally related and subordinate facilities (such as parking, common areas, and facilities for use by tenants). Units must be used on other than a transient basis. Facilities not functionally related and subordinate, or units made available to the general public on a transient basis (for example, hotel or motel use), can cause the Project to fail to qualify."));
        out.push(para("**Single-room occupancy and certain exceptions.** Single-room occupancy housing and certain residential rental projects providing supportive services may qualify notwithstanding the general non-transient requirement, subject to the applicable conditions. Whether a particular Project qualifies under one of these exceptions should be confirmed with bond counsel."));
      }
      out.push(para("Any proposed change in the use of a Project, sale or transfer of a Project, or change in the property manager should be discussed with bond counsel before it occurs, because such changes can affect the tax-exempt status of the Bonds."));
      return out;
    }
    case "pbu":
      return [
        para("Interest on the Bonds is excluded from gross income only if the financed facilities are not used for a private business use beyond the limits permitted by Sections 141 and 145 of the Code. Use by the 501(c)(3) Borrower in an unrelated trade or business, and use by other nongovernmental persons other than as a member of the general public, are treated as private business use and are subject to limitation."),
        para("**What counts as private business use.** Private business use generally arises when a nongovernmental person, or a 501(c)(3) organization acting outside the scope of its exempt purpose, uses bond-financed property other than as a member of the general public. Common sources include leases, management or service contracts, research agreements, naming rights, and other special legal entitlements to use the financed space. For these purposes, a Section 501(c)(3) organization using the property in furtherance of its exempt purpose is generally treated like a governmental unit."),
        para("**Management and service contracts.** A management or service contract with respect to bond-financed property can give rise to private business use unless it satisfies the safe harbor conditions of Revenue Procedure 2017-13. In general, those conditions require that the compensation be reasonable and not based on a share of net profits from the operation of the facility, that the term not exceed the limits in the revenue procedure, and that the qualified user exercise a significant degree of control over the use of the property and bear the risk of loss. A copy of Revenue Procedure 2017-13 is included as an exhibit to this Manual."),
        para("**Monitoring and escalation.** The Federal Tax Compliance Officer should review each new or renewed lease, management contract, service contract, or research agreement affecting the financed facilities before it is executed, and should consult bond counsel where any arrangement may give rise to private business use. Any unusual advance, prepayment, or lending arrangement involving bond proceeds or the financed facilities should be escalated to bond counsel before it is entered into."),
      ];
    case "exhibit-8703":
      return [
        para("A blank copy of IRS Form 8703, Annual Certification of a Residential Rental Project, is attached behind this page for use in making the annual set-aside certification."),
      ];
    case "exhibit-2004-39":
      return [
        para("A copy of Revenue Procedure 2004-39 is attached behind this page. It sets forth the procedures for determining whether a residential rental project satisfies the Section 142(d) set-aside requirements during the qualified project period, including the transition period applicable to acquired projects."),
      ];
    case "exhibit-2017-13":
      return [
        para("A copy of Revenue Procedure 2017-13 is attached behind this page. It sets forth the safe harbor conditions under which a management or service contract does not result in private business use of bond-financed property."),
      ];
    case "exhibit-irs-guide":
      return [
        para("A copy of the IRS Tax Compliance Guide is attached behind this page as a general reference on post-issuance compliance for tax-exempt bonds."),
      ];
    case "exhibit-management-contracts":
      return [
        para("The Borrower's executed management and service contracts affecting the bond-financed facilities are attached behind this page. Each such contract should be reviewed against the private business use rules and the safe harbor conditions of Revenue Procedure 2017-13."),
      ];
    case "arbitrage":
      return [
        para("The bonds are subject to the arbitrage and rebate requirements of Section 148 of the Code. In general, the Borrower may not invest bond proceeds at a yield materially higher than the yield on the bonds, except during permitted temporary periods, and must pay to the United States any arbitrage profit that is required to be rebated."),
        bullet("Track the investment and expenditure of all gross proceeds, including any reserve funds."),
        bullet("Determine whether a spending exception to rebate applies and, if so, document satisfaction of the applicable spending milestones."),
        bullet("Compute and pay any required rebate on each installment computation date (generally every five years) and on the final redemption of the bonds."),
        para("The Borrower should engage a qualified rebate analyst as needed and retain the resulting computations as described in the recordkeeping section."),
      ];
    case "records":
      return [
        para("The Borrower must maintain records sufficient to establish continuing compliance with the federal tax requirements. Records must be retained for the life of the bond issue, plus the life of any bond issue that refunds it, plus three years."),
        para("Records to be retained include, at a minimum:"),
        bullet("the transcript of proceedings for each bond issue, including the tax certificate;"),
        bullet("records of the expenditure of bond proceeds;"),
        bullet("tenant income certifications and the data supporting each annual set-aside determination;"),
        bullet("each filed Form 8703 and proof of filing; and"),
        bullet("investment records and any rebate computations."),
      ];
    case "remediation":
      return [
        para("A potential Section 142(d) failure requires immediate analysis. The Borrower may not assume that later restoration of the elected set-aside, a generic remedial action, or a voluntary filing will retroactively cure the failure."),
        para("If the Federal Tax Compliance Officer identifies a potential failure to comply with the federal tax requirements, the Borrower should promptly consult bond counsel and preserve all relevant records. The remedial-action regime under Treasury Regulations Section 1.141-12 does not generally apply to a Section 145(d) or Section 142(d) failure; Section 142(d) noncompliance is governed by its own reasonable-period correction rule and the acquisition-transition rule of Revenue Procedure 2004-39. A sale or other loss of Section 501(c)(3) ownership may, by contrast, have remedies under Section 1.141-12, but those remedies are highly time-sensitive and issuer-driven and must be pursued immediately with bond counsel."),
        para("Where available, a violation may also be resolved through the IRS Tax-Exempt Bonds Voluntary Closing Agreement Program (VCAP). Prompt identification and correction generally produces a more favorable resolution than a violation discovered on examination."),
      ];
    default:
      return [];
  }
}

function buildManualDoc(form, sections) {
  const name = form.borrowerName || "[Borrower Name]";
  const children = [];

  // COVER
  children.push(new Paragraph({ spacing: { after: 80 }, children: [new TextRun({ text: "FEDERAL TAX POST-ISSUANCE COMPLIANCE MANUAL", bold: true, size: 20, color: "1F4E6B" })] }));
  children.push(new Paragraph({ spacing: { before: 240, after: 120 }, alignment: AlignmentType.CENTER, children: [new TextRun({ text: name, bold: true, size: 44 })] }));
  children.push(new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 60 }, children: [new TextRun({ text: "Federal Tax Post-Issuance Compliance Manual", size: 26, color: "5A6473" })] }));
  children.push(new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: form.lastUpdated ? 60 : 120 }, children: [new TextRun({ text: `Date Adopted: ${fmtDate(form.manualDate)}`, size: 22, color: "5A6473" })] }));
  if (form.lastUpdated) {
    children.push(new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 120 }, children: [new TextRun({ text: `Last Updated: ${fmtDate(form.lastUpdated)}`, size: 20, color: "5A6473" })] }));
  }
  children.push(new Paragraph({ spacing: { before: 480, after: 120 }, children: [new TextRun({ text: "PURPOSE, SCOPE, AND SIGNIFICANCE", bold: true, size: 22, color: "1F4E6B" })] }));
  significanceParagraphs(form.borrowerName).forEach((p) => children.push(para(p)));

  // PROJECT COMPLIANCE PROFILE (one page per project)
  (form.bonds || []).forEach((b, i) => {
    children.push(new Paragraph({ children: [new PageBreak()] }));
    children.push(new Paragraph({ spacing: { after: 240 }, children: [new TextRun({ text: "PROJECT COMPLIANCE PROFILE", bold: true, size: 22, color: "1F4E6B" })] }));
    children.push(new Paragraph({ spacing: { after: 240 }, children: [new TextRun({ text: b.projectName || b.issueName || `Project ${i + 1}`, bold: true, size: 26 })] }));
    children.push(infoTable([
      ["Project / address", [b.projectName, b.projectLocation].filter(Boolean).join(", ") || "____________________"],
      ["Bond issue", b.issueName || "____________________"],
      ["Issue date", b.issueDate ? fmtDate(b.issueDate) : "____________________"],
      ["Issuer / conduit borrower / owner", [b.issuer, b.conduitBorrowerOwner].filter(Boolean).join(" / ") || "____________________"],
      ["Form 8703 operator", b.form8703Operator || "____________________"],
      ["Current property manager", b.propertyManager || "____________________"],
      ["Section 142(d) election", b.setAsideElection || "____________________"],
      ["Total residential units", b.units || "____________________"],
      ["Conservative low-income-unit target", b.lowIncomeTarget || "____________________"],
      ["Qualified project period began", b.qppBegan ? fmtDate(b.qppBegan) : "____________________"],
      ["Earliest possible end of qualified project period", b.qppEarliestEnd ? fmtDate(b.qppEarliestEnd) : "____________________"],
      ["Other outstanding tax-exempt bonds for project", b.otherBondsForProject || "____________________"],
      ["Section 8 / HUD assistance", b.section8Hud || "____________________"],
      ["LIHTC status / BINs", b.lihtcStatus || "____________________"],
      ["Latest Form 8703 filing date and proof of filing", b.latest8703Filing || "____________________"],
      ["Tax Certificate / Regulatory Agreement location", b.taxCertRegAgreementLocation || "____________________"],
      ["Rebate status / next computation date", b.rebateStatusNextDate || "____________________"],
      ["Bond counsel / rebate consultant contacts", b.bondCounselRebateContacts || "____________________"],
    ]));
    if (b.transitionPeriodUsed === "yes") {
      children.push(para(`Rev. Proc. 2004-39 transition period: used. The Project first satisfied the elected set-aside on ${b.transitionPeriodSatisfiedDate ? fmtDate(b.transitionPeriodSatisfiedDate) : "____________________"}.`));
    } else {
      children.push(para("Rev. Proc. 2004-39 transition period: not used."));
    }
  });

  children.push(new Paragraph({ children: [new PageBreak()] }));

  // TOC
  children.push(new Paragraph({ spacing: { after: 160 }, children: [new TextRun({ text: "Table of Contents", bold: true, size: 32 })] }));
  const tocNoBorder = { style: BorderStyle.NONE, size: 0, color: "FFFFFF" };
  sections.forEach((s, i) => {
    const prev = sections[i - 1];
    if (s.isExhibit && (!prev || !prev.isExhibit)) {
      children.push(new Paragraph({ spacing: { before: 200, after: 80 }, children: [new TextRun({ text: "EXHIBITS", bold: true, size: 22, color: "1F4E6B" })] }));
    }
    const lead = s.isExhibit ? `Exhibit ${s.exhibitLetter}.  ${s.title}` : `${s.displayNum}.  ${s.title}`;
    // Keep each entry's title and description on the same page by placing both
    // in a single cantSplit table row (borderless, so it reads as plain text).
    children.push(new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        top: tocNoBorder, bottom: tocNoBorder, left: tocNoBorder, right: tocNoBorder,
        insideVertical: tocNoBorder, insideHorizontal: tocNoBorder,
      },
      rows: [
        new TableRow({
          cantSplit: true,
          children: [
            new TableCell({
              width: { size: 100, type: WidthType.PERCENTAGE },
              margins: { top: 0, bottom: 0, left: 0, right: 0 },
              children: [
                new Paragraph({ spacing: { before: 120, after: 40 }, children: [new TextRun({ text: lead, bold: true, size: 24 })] }),
                new Paragraph({ spacing: { after: 80 }, indent: { left: 360 }, children: [new TextRun({ text: smartQuotes(s.toc), size: 21, color: "5A6473" })] }),
              ],
            }),
          ],
        }),
      ],
    }));
  });

  // ADOPTION (placed immediately after the table of contents)
  children.push(new Paragraph({ pageBreakBefore: true, spacing: { after: 160 }, children: [new TextRun({ text: "Adoption", bold: true, size: 32 })] }));
  children.push(para(`These policies and procedures were adopted by ${name} as of ${fmtDate(form.manualDate)}.`));
  children.push(new Paragraph({ spacing: { before: 720, after: 60 }, children: [new TextRun({ text: "____________________________________", size: 24 })] }));
  children.push(new Paragraph({ spacing: { after: 360 }, children: [new TextRun({ text: "Authorized Officer", size: 22 })] }));
  children.push(new Paragraph({ spacing: { after: 60 }, children: [new TextRun({ text: "____________________________________", size: 24 })] }));
  children.push(new Paragraph({ spacing: { after: 480 }, children: [new TextRun({ text: "Title", size: 22 })] }));
  // Last reviewed box (filled in by hand on each periodic review)
  children.push(new Paragraph({ spacing: { after: 120 }, children: [new TextRun({ text: "Annual / Periodic Review", bold: true, size: 24, color: "1F4E6B" })] }));
  children.push(para("This Manual should be reviewed periodically. Record each review below."));
  children.push(reviewTable());

  // SECTIONS
  // Each section starts on its own page via an explicit hard page break, then
  // its heading and first paragraph are locked together so the heading can
  // never sit alone at the foot of a page.
  sections.forEach((s, idx) => {
    const bodyNodes = sectionBody(s.id, form);
    const firstBody = bodyNodes.length ? bodyNodes[0] : null;
    children.push(new Paragraph({ children: [new PageBreak()] }));
    children.push(lockedSectionStart(s.label || `SECTION ${s.displayNum}`, s.title, firstBody, { pageBreakBefore: false }));
    bodyNodes.slice(1).forEach((node) => children.push(node));
  });

  const doc = new Document({
    creator: "PublicFinanceTax.com",
    title: `${name} Federal Tax Post-Issuance Compliance Manual`,
    styles: { default: { document: { run: { font: "Calibri", size: 22 } } } },
    sections: [{
      properties: { page: { margin: { top: 1440, bottom: 1440, left: 1440, right: 1440 } } },
      children,
    }],
  });
  return doc;
}


// ---------------------------------------------------------------------------
// Static section descriptions used in the Table of Contents and as section
// intros. These are the boilerplate descriptions that explain each section.
// ---------------------------------------------------------------------------
const SECTION_LIBRARY = [
  {
    id: "responsibility",
    n: 1,
    title: "Post-Issuance Compliance Administration",
    toc:
      "Designates the Federal Tax Compliance Officer, describes delegation and " +
      "the Form 8703 operator, sets the annual compliance review and property " +
      "manager reporting duties, and establishes the matters that require " +
      "consultation with bond counsel before they occur.",
  },
  {
    id: "bonds",
    n: 2,
    title: "List of Outstanding Tax-Exempt Bonds",
    toc:
      "A schedule of each outstanding qualified 501(c)(3) bond issue, including " +
      "issue name, date of issue, issuer, principal amount, the financed project, " +
      "and final maturity.",
  },
  {
    id: "certs",
    n: 3,
    title: "Tax Certificates",
    toc:
      "The executed tax certificate (or tax compliance and arbitrage certificate) " +
      "for each bond issue, setting forth the borrower's representations and " +
      "covenants regarding the federal tax requirements applicable to the bonds.",
  },
  {
    id: "setaside-explanation",
    n: 4,
    title: "Residential Rental Set-Aside Requirements",
    toc:
      "A general explanation of the low-income occupancy set-aside applicable to " +
      "the financed projects under section 142(d) (incorporated through the " +
      "section 145(d)(2)(B) exception), including the 20-50 and 40-60 elections, " +
      "the qualified project period, the available-unit rule, and the annual " +
      "certification and filing obligations under Revenue Procedure 2004-39.",
  },
  {
    id: "form8703-explanation",
    n: 5,
    title: "IRS Form 8703 \u2014 Annual Certification",
    toc:
      "A description of IRS Form 8703 (Annual Certification of a Residential " +
      "Rental Project), who must file it, the information it requires, and the " +
      "annual filing deadline.",
  },
  {
    id: "form8703-copies",
    n: 6,
    title: "IRS Form 8703 \u2014 Copies for Each Financing",
    toc:
      "A copy of the most recently filed Form 8703 for each financed project, " +
      "retained as part of the compliance record.",
  },
  {
    id: "use-restrictions",
    n: 7,
    title: "Qualified Residential Rental Project Use Restrictions",
    toc:
      "The continuing operational requirements each project must satisfy for the " +
      "life of the bonds, including the requirement that units be available to the " +
      "general public on a continuous, non-transient rental basis and that the " +
      "low-income set-aside be maintained throughout the qualified project period.",
  },
  {
    id: "pbu",
    n: 8,
    title: "Private Business Use",
    toc:
      "An explanation of the private-business-use limitations applicable to " +
      "qualified 501(c)(3) bonds under sections 141 and 145, the management and " +
      "service contract safe harbors of Revenue Procedure 2017-13, and the " +
      "circumstances under which private use of the financed facilities must be " +
      "monitored and reported.",
  },
  {
    id: "arbitrage",
    n: 9,
    title: "Arbitrage and Rebate",
    toc:
      "A summary of the arbitrage yield restriction and rebate requirements under " +
      "section 148, including investment of proceeds, rebate computation dates, " +
      "and any applicable spending exceptions.",
  },
  {
    id: "records",
    n: 10,
    title: "Recordkeeping and Record Retention",
    toc:
      "The records that must be maintained and the retention period, generally " +
      "the life of the bond issue, plus the life of any issue that refunds it, plus " +
      "three years.",
  },
  {
    id: "remediation",
    n: 11,
    title: "Identifying and Correcting Noncompliance",
    toc:
      "Procedures for identifying potential violations and available corrective " +
      "measures, including the IRS Voluntary Closing Agreement Program (VCAP).",
  },
  {
    id: "exhibit-8703",
    n: 12,
    exhibit: true,
    title: "IRS Form 8703",
    toc:
      "A blank copy of IRS Form 8703, Annual Certification of a Residential " +
      "Rental Project, for use in making the annual set-aside certification.",
  },
  {
    id: "exhibit-2004-39",
    n: 13,
    exhibit: true,
    title: "Revenue Procedure 2004-39",
    toc:
      "A copy of Revenue Procedure 2004-39, setting forth the procedures for " +
      "determining whether a residential rental project satisfies the section " +
      "142(d) set-aside requirements during the qualified project period.",
  },
  {
    id: "exhibit-2017-13",
    n: 14,
    exhibit: true,
    title: "Revenue Procedure 2017-13",
    toc:
      "A copy of Revenue Procedure 2017-13, setting forth the safe harbor " +
      "conditions under which a management or service contract does not result in " +
      "private business use of bond-financed property.",
  },
  {
    id: "exhibit-irs-guide",
    n: 15,
    exhibit: true,
    title: "IRS Tax Compliance Guide",
    toc:
      "A copy of the IRS Tax Compliance Guide, provided as a general reference on " +
      "post-issuance compliance for tax-exempt bonds.",
  },
  {
    id: "exhibit-management-contracts",
    n: 16,
    exhibit: true,
    title: "Management Contracts",
    toc:
      "The Borrower's executed management and service contracts affecting the " +
      "bond-financed facilities, retained for review against the private business " +
      "use rules and the safe harbor conditions of Revenue Procedure 2017-13.",
  },
];

// Which sections are always on versus attorney-selectable
const CORE_SECTION_IDS = SECTION_LIBRARY.map((s) => s.id);

const emptyBond = () => ({
  issueName: "",
  issueDate: "",
  issuer: "",
  principalAmount: "",
  projectName: "",
  projectLocation: "",
  units: "",
  setAsideElection: "40-60",
  detailLevel: "plain", // "plain" | "detailed", per issue
  finalMaturity: "",
  notes: "",
  // Project Compliance Profile fields
  conduitBorrowerOwner: "",
  form8703Operator: "",
  propertyManager: "",
  lowIncomeTarget: "",
  qppBegan: "",
  qppEarliestEnd: "",
  otherBondsForProject: "",
  section8Hud: "",
  lihtcStatus: "",
  latest8703Filing: "",
  taxCertRegAgreementLocation: "",
  rebateStatusNextDate: "",
  bondCounselRebateContacts: "",
  transitionPeriodUsed: "no", // "yes" | "no"
  transitionPeriodSatisfiedDate: "",
});

const initialForm = {
  borrowerName: "",
  borrowerType: "501(c)(3) organization",
  responsibleParty: "",
  responsibleTitle: "",
  responsibleContact: "",
  // Federal Tax Compliance Officer (Section 1 / cover)
  ftcoName: "",
  ftcoTitle: "",
  ftcoContact: "",
  manualDate: new Date().toISOString().slice(0, 10),
  lastUpdated: new Date().toISOString().slice(0, 10),
  bonds: [emptyBond()],
  includedSections: CORE_SECTION_IDS.reduce((a, id) => ({ ...a, [id]: true }), {}),
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
function ComplianceManualBuilder() {
  const [form, setForm] = useState(initialForm);
  const [view, setView] = useState("form"); // "form" | "manual"
  const manualRef = useRef(null);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setBond = (i, k, v) =>
    setForm((f) => {
      const bonds = f.bonds.map((b, idx) => (idx === i ? { ...b, [k]: v } : b));
      return { ...f, bonds };
    });

  const addBond = () =>
    setForm((f) => ({ ...f, bonds: [...f.bonds, emptyBond()] }));

  const removeBond = (i) =>
    setForm((f) => ({
      ...f,
      bonds: f.bonds.length > 1 ? f.bonds.filter((_, idx) => idx !== i) : f.bonds,
    }));

  const toggleSection = (id) =>
    setForm((f) => ({
      ...f,
      includedSections: { ...f.includedSections, [id]: !f.includedSections[id] },
    }));

  const sections = (() => {
    let secNum = 0;
    let exhIdx = 0;
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    return SECTION_LIBRARY.filter((s) => form.includedSections[s.id]).map((s) => {
      if (s.exhibit) {
        const letter = letters[exhIdx] || `Ex${exhIdx + 1}`;
        exhIdx += 1;
        return { ...s, isExhibit: true, exhibitLetter: letter, displayNum: letter, label: `EXHIBIT ${letter}` };
      }
      secNum += 1;
      return { ...s, isExhibit: false, displayNum: secNum, label: `SECTION ${secNum}` };
    });
  })();

  const handleDownloadWord = async () => {
    try {
      const doc = buildManualDoc(form, sections);
      const blob = await Packer.toBlob(doc);
      const safe = (form.borrowerName || "Borrower").replace(/[^\w\- ]+/g, "").trim() || "Borrower";
      saveAs(blob, `${safe} - Federal Tax Post-Issuance Compliance Manual.docx`);
    } catch (e) {
      alert("Could not generate the Word document: " + (e && e.message ? e.message : e));
    }
  };

  const fmtDate = (d) => {
    if (!d) return "____________";
    const dt = new Date(d + "T00:00:00");
    if (isNaN(dt)) return d;
    return dt.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // -------------------------------------------------------------------------
  // STYLES
  // -------------------------------------------------------------------------
  const C = {
    ink: "#1a1f2b",
    sub: "#5a6473",
    line: "#d4d9e0",
    accent: "#1f4e6b",
    paper: "#ffffff",
    field: "#f7f8fa",
  };

  return (
    <div style={{ fontFamily: "Georgia, 'Times New Roman', serif", color: C.ink }}>
      <style>{`
        * { box-sizing: border-box; }
        .ui { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
        input, select, textarea {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-size: 14px; padding: 9px 11px; border: 1px solid ${C.line};
          border-radius: 6px; background: ${C.field}; width: 100%; color: ${C.ink};
        }
        input:focus, select:focus, textarea:focus {
          outline: 2px solid ${C.accent}; outline-offset: -1px; background: #fff;
        }
        label.fl { display:block; font-size:12px; font-weight:600; color:${C.sub};
          margin-bottom:5px; text-transform:uppercase; letter-spacing:.04em; }
        .btn { font-family: -apple-system, sans-serif; cursor:pointer; border:none;
          border-radius:6px; padding:11px 18px; font-size:14px; font-weight:600; }
        .btn-primary { background:${C.accent}; color:#fff; }
        .btn-ghost { background:#eef1f5; color:${C.ink}; }
        .btn-danger { background:#fff; color:#b23b3b; border:1px solid #e3b5b5; padding:6px 10px; font-size:12px; }

        /* Print rules */
        @media print {
          .no-print { display: none !important; }
          .manual { box-shadow:none !important; margin:0 !important; }
          .page-break { page-break-before: always; }
          body { background:#fff; }
        }
        @page { size: letter; margin: 1in; }
      `}</style>

      {view === "form" && (
        <FormView
          C={C}
          form={form}
          setField={setField}
          setBond={setBond}
          addBond={addBond}
          removeBond={removeBond}
          toggleSection={toggleSection}
          onGenerate={() => setView("manual")}
        />
      )}

      {view === "manual" && (
        <ManualView
          C={C}
          form={form}
          sections={sections}
          fmtDate={fmtDate}
          manualRef={manualRef}
          onBack={() => setView("form")}
          onDownloadWord={handleDownloadWord}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FORM VIEW
// ---------------------------------------------------------------------------
function FormView({
  C, form, setField, setBond, addBond, removeBond, toggleSection, onGenerate,
}) {
  return (
    <div className="ui" style={{ maxWidth: 920, margin: "0 auto", padding: "28px 22px 140px", height: "calc(100vh - 60px)", overflowY: "auto" }}>
      <div style={{ borderBottom: `3px solid ${C.accent}`, paddingBottom: 14, marginBottom: 26 }}>
        <div style={{ fontSize: 12, letterSpacing: ".12em", textTransform: "uppercase", color: C.accent, fontWeight: 700 }}>
          PublicFinanceTax.com
        </div>
        <h1 style={{ fontFamily: "Georgia, serif", fontSize: 27, margin: "6px 0 4px", color: C.ink }}>
          Post-Issuance Compliance Manual Builder
        </h1>
        <p style={{ margin: 0, color: C.sub, fontSize: 14 }}>
          Qualified 501(c)(3) bonds financing acquired residential rental property
          for Section 145(d)(2)(B) and Section 142(d) projects.
        </p>
      </div>

      {/* Borrower block */}
      <Card C={C} title="Borrower">
        <Grid>
          <Field label="Borrower Name" span={2}>
            <input value={form.borrowerName} onChange={(e) => setField("borrowerName", e.target.value)}
              placeholder="e.g., Riverside Senior Housing, Inc." />
          </Field>
          <Field label="Borrower Type">
            <input value={form.borrowerType} onChange={(e) => setField("borrowerType", e.target.value)} />
          </Field>
          <Field label="Manual Date">
            <input type="date" value={form.manualDate} onChange={(e) => setField("manualDate", e.target.value)} />
          </Field>
          <Field label="Last Updated">
            <input type="date" value={form.lastUpdated} onChange={(e) => setField("lastUpdated", e.target.value)} />
          </Field>
        </Grid>
      </Card>

      {/* Responsible party */}
      <Card C={C} title="Federal Tax Compliance Officer"
        sub="The internal officer of the Borrower responsible for administering these policies. This should be an officer of the Borrower, not outside bond counsel.">
        <Grid>
          <Field label="Federal Tax Compliance Officer">
            <input value={form.ftcoName} onChange={(e) => setField("ftcoName", e.target.value)}
              placeholder="e.g., Jane Doe" />
          </Field>
          <Field label="Title">
            <input value={form.ftcoTitle} onChange={(e) => setField("ftcoTitle", e.target.value)}
              placeholder="e.g., Chief Financial Officer" />
          </Field>
          <Field label="Email / Telephone" span={2}>
            <input value={form.ftcoContact} onChange={(e) => setField("ftcoContact", e.target.value)} />
          </Field>
        </Grid>
      </Card>

      {/* Bonds */}
      <Card C={C} title="Outstanding Bond Issues"
        sub="Add each outstanding qualified 501(c)(3) bond issue and the project it financed.">
        {form.bonds.map((b, i) => (
          <div key={i} style={{ border: `1px solid ${C.line}`, borderRadius: 8, padding: 16, marginBottom: 14, background: "#fcfcfd" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <strong style={{ fontSize: 13, color: C.accent }}>Bond Issue {i + 1}</strong>
              <button className="btn btn-danger" onClick={() => removeBond(i)}>Remove</button>
            </div>
            <Grid>
              <Field label="Issue Name" span={2}>
                <input value={b.issueName} onChange={(e) => setBond(i, "issueName", e.target.value)}
                  placeholder="e.g., $12,500,000 Health and Educational Facilities Revenue Bonds, Series 2023A" />
              </Field>
              <Field label="Issuer">
                <input value={b.issuer} onChange={(e) => setBond(i, "issuer", e.target.value)}
                  placeholder="Conduit issuer or authority" />
              </Field>
              <Field label="Date of Issue">
                <input type="date" value={b.issueDate} onChange={(e) => setBond(i, "issueDate", e.target.value)} />
              </Field>
              <Field label="Principal Amount">
                <input value={b.principalAmount} onChange={(e) => setBond(i, "principalAmount", e.target.value)}
                  placeholder="$" />
              </Field>
              <Field label="Final Maturity">
                <input type="date" value={b.finalMaturity} onChange={(e) => setBond(i, "finalMaturity", e.target.value)} />
              </Field>
              <Field label="Project Name" span={2}>
                <input value={b.projectName} onChange={(e) => setBond(i, "projectName", e.target.value)}
                  placeholder="Name of financed project" />
              </Field>
              <Field label="Project Location" span={2}>
                <input value={b.projectLocation} onChange={(e) => setBond(i, "projectLocation", e.target.value)}
                  placeholder="Address, city, state" />
              </Field>
              <Field label="Total Residential Units">
                <input value={b.units} onChange={(e) => setBond(i, "units", e.target.value)} placeholder="e.g., 120" />
              </Field>
              <Field label="Set-Aside Election">
                <select value={b.setAsideElection} onChange={(e) => setBond(i, "setAsideElection", e.target.value)}>
                  <option value="40-60">40-60 (40% at 60% AMI)</option>
                  <option value="20-50">20-50 (20% at 50% AMI)</option>
                  <option value="Average Income">Average Income test</option>
                </select>
              </Field>
              <Field label="142(d) Detail Level for This Issue" span={2}>
                <select value={b.detailLevel} onChange={(e) => setBond(i, "detailLevel", e.target.value)}>
                  <option value="plain">Plain: client-education summary (single project, general audience)</option>
                  <option value="detailed">Detailed: full mechanics (sophisticated operator)</option>
                </select>
              </Field>
              <Field label="Notes" span={2}>
                <textarea rows={2} value={b.notes} onChange={(e) => setBond(i, "notes", e.target.value)}
                  placeholder="Optional: qualified project period dates, refunding info, etc." />
              </Field>

              <Field label="" span={2}>
                <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", color: C.accent, marginTop: 6, marginBottom: 2 }}>
                  Project Compliance Profile
                </div>
              </Field>
              <Field label="Conduit Borrower / Owner">
                <input value={b.conduitBorrowerOwner} onChange={(e) => setBond(i, "conduitBorrowerOwner", e.target.value)} />
              </Field>
              <Field label="Form 8703 Operator">
                <input value={b.form8703Operator} onChange={(e) => setBond(i, "form8703Operator", e.target.value)}
                  placeholder="Person delegated 142(d) compliance" />
              </Field>
              <Field label="Current Property Manager">
                <input value={b.propertyManager} onChange={(e) => setBond(i, "propertyManager", e.target.value)} />
              </Field>
              <Field label="Conservative Low-Income-Unit Target">
                <input value={b.lowIncomeTarget} onChange={(e) => setBond(i, "lowIncomeTarget", e.target.value)}
                  placeholder="e.g., 50 units" />
              </Field>
              <Field label="Qualified Project Period Began">
                <input type="date" value={b.qppBegan} onChange={(e) => setBond(i, "qppBegan", e.target.value)} />
              </Field>
              <Field label="Earliest Possible End of QPP">
                <input type="date" value={b.qppEarliestEnd} onChange={(e) => setBond(i, "qppEarliestEnd", e.target.value)} />
              </Field>
              <Field label="Other Outstanding Tax-Exempt Bonds for Project" span={2}>
                <input value={b.otherBondsForProject} onChange={(e) => setBond(i, "otherBondsForProject", e.target.value)} />
              </Field>
              <Field label="Section 8 / HUD Assistance">
                <input value={b.section8Hud} onChange={(e) => setBond(i, "section8Hud", e.target.value)} />
              </Field>
              <Field label="LIHTC Status / BINs">
                <input value={b.lihtcStatus} onChange={(e) => setBond(i, "lihtcStatus", e.target.value)} />
              </Field>
              <Field label="Latest Form 8703 Filing Date and Proof" span={2}>
                <input value={b.latest8703Filing} onChange={(e) => setBond(i, "latest8703Filing", e.target.value)} />
              </Field>
              <Field label="Tax Certificate / Regulatory Agreement Location" span={2}>
                <input value={b.taxCertRegAgreementLocation} onChange={(e) => setBond(i, "taxCertRegAgreementLocation", e.target.value)} />
              </Field>
              <Field label="Rebate Status / Next Computation Date" span={2}>
                <input value={b.rebateStatusNextDate} onChange={(e) => setBond(i, "rebateStatusNextDate", e.target.value)} />
              </Field>
              <Field label="Bond Counsel / Rebate Consultant Contacts" span={2}>
                <input value={b.bondCounselRebateContacts} onChange={(e) => setBond(i, "bondCounselRebateContacts", e.target.value)} />
              </Field>
              <Field label="Rev. Proc. 2004-39 Transition Period Used?">
                <select value={b.transitionPeriodUsed} onChange={(e) => setBond(i, "transitionPeriodUsed", e.target.value)}>
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </Field>
              {b.transitionPeriodUsed === "yes" && (
                <Field label="Date Project First Satisfied Set-Aside">
                  <input type="date" value={b.transitionPeriodSatisfiedDate} onChange={(e) => setBond(i, "transitionPeriodSatisfiedDate", e.target.value)} />
                </Field>
              )}
            </Grid>
          </div>
        ))}
        <button className="btn btn-ghost" onClick={addBond}>+ Add another bond issue</button>
      </Card>

      {/* Sections */}
      <Card C={C} title="Sections to Include"
        sub="Uncheck any section you do not want in this borrower's manual.">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {SECTION_LIBRARY.map((s) => (
            <label key={s.id} style={{
              display: "flex", gap: 9, alignItems: "flex-start", padding: "9px 11px",
              border: `1px solid ${C.line}`, borderRadius: 6, fontSize: 13.5,
              background: form.includedSections[s.id] ? "#eef4f8" : "#fff", cursor: "pointer",
            }}>
              <input type="checkbox" style={{ width: "auto", marginTop: 2 }}
                checked={!!form.includedSections[s.id]} onChange={() => toggleSection(s.id)} />
              <span>{s.title}</span>
            </label>
          ))}
        </div>
      </Card>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 8 }}>
        <button className="btn btn-primary" onClick={onGenerate}>Generate Manual &rarr;</button>
      </div>
    </div>
  );
}

const Card = ({ C, title, sub, children }) => (
  <div style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 10, padding: 20, marginBottom: 18 }}>
    <h2 style={{ fontFamily: "Georgia, serif", fontSize: 18, margin: "0 0 2px", color: C.ink }}>{title}</h2>
    {sub && <p style={{ margin: "0 0 14px", color: C.sub, fontSize: 13 }}>{sub}</p>}
    {!sub && <div style={{ height: 12 }} />}
    {children}
  </div>
);

const Grid = ({ children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>{children}</div>
);

const Field = ({ label, span = 1, children }) => (
  <div style={{ gridColumn: span === 2 ? "1 / -1" : "auto" }}>
    <label className="fl">{label}</label>
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// MANUAL VIEW (print-ready)
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Stage two: assemble the final merged PDF (edited Word manual + exhibit PDFs)
// ---------------------------------------------------------------------------
function MergePanel({ C, form, sections }) {
  const [open, setOpen] = React.useState(false);
  const [editedManual, setEditedManual] = React.useState(null);
  const [certFiles, setCertFiles] = React.useState([]);
  const [refsAvailable, setRefsAvailable] = React.useState({});
  const [exhibits, setExhibits] = React.useState({
    tax_certificates: true,
  });
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");

  // Only show exhibit toggles for exhibits actually included in this manual.
  const has = (id) => sections.some((s) => s.id === id);

  React.useEffect(() => {
    if (!open) return;
    fetch("/api/compliance-manual/references")
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => setRefsAvailable(d || {}))
      .catch(() => setRefsAvailable({}));
  }, [open]);

  const toggle = (k) => setExhibits((e) => ({ ...e, [k]: !e[k] }));

  const buildPdf = async () => {
    setError("");
    if (!editedManual) {
      setError("Upload your finalized Word manual first.");
      return;
    }
    setBusy(true);
    try {
      const idToKey = {
        "exhibit-8703": "form8703",
        "exhibit-2004-39": "rev_proc_2004-39",
        "exhibit-2017-13": "rev_proc_2017-13",
        "exhibit-irs-guide": "irs_tax_compliance_guide",
        "exhibit-management-contracts": "tax_certificates",
      };
      const exhibitLetters = {};
      sections.forEach((s) => {
        if (s.isExhibit && idToKey[s.id]) {
          exhibitLetters[idToKey[s.id]] = (s.exhibitLetter || "").toLowerCase();
        }
      });

      // The four fixed references merge automatically whenever their exhibit is
      // present in the manual. Tax certificates merge only if the user checked
      // the box and uploaded files.
      const mergeExhibits = {
        form8703: has("exhibit-8703"),
        "rev_proc_2004-39": has("exhibit-2004-39"),
        "rev_proc_2017-13": has("exhibit-2017-13"),
        irs_tax_compliance_guide: has("exhibit-irs-guide"),
        tax_certificates: has("exhibit-management-contracts") && !!exhibits.tax_certificates,
      };

      const fd = new FormData();
      fd.append("manual", editedManual);
      fd.append("manifest", JSON.stringify({
        borrowerName: form.borrowerName || "Borrower",
        exhibits: mergeExhibits,
        exhibitLetters,
      }));
      certFiles.forEach((f) => fd.append("tax_certificates", f));

      const resp = await fetch("/api/compliance-manual/merge", { method: "POST", body: fd });
      if (!resp.ok) {
        let msg = `Merge failed (${resp.status})`;
        try { const j = await resp.json(); if (j.detail) msg = j.detail; } catch (e) {}
        throw new Error(msg);
      }
      const blob = await resp.blob();
      const safe = (form.borrowerName || "Borrower").replace(/[^\w\- ]+/g, "").trim() || "Borrower";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safe} - Federal Tax Post-Issuance Compliance Manual.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const fileRow = (label, files, setFiles, accept) => (
    <div style={{ marginBottom: 12 }}>
      <label className="fl" style={{ display: "block", marginBottom: 5 }}>{label}</label>
      <input type="file" accept={accept} multiple
        onChange={(e) => setFiles(Array.from(e.target.files || []))} />
      {files.length > 0 && (
        <div style={{ fontSize: 12, color: C.sub, marginTop: 4 }}>
          {files.map((f) => f.name).join(", ")}
        </div>
      )}
    </div>
  );

  const checkRow = (key, label, available) => (
    <label style={{ display: "flex", gap: 9, alignItems: "center", padding: "6px 0", fontSize: 13.5,
      opacity: available === false ? 0.5 : 1 }}>
      <input type="checkbox" style={{ width: "auto" }}
        checked={!!exhibits[key]} disabled={available === false}
        onChange={() => toggle(key)} />
      <span>{label}</span>
      {available === false && (
        <span style={{ fontSize: 11, color: "#b23b3b" }}>(not on server)</span>
      )}
    </label>
  );

  return (
    <div className="no-print ui" style={{ maxWidth: 816, margin: "18px auto 0", padding: "0 8px" }}>
      <div style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 10, overflow: "hidden" }}>
        <button onClick={() => setOpen((o) => !o)} style={{
          width: "100%", textAlign: "left", border: "none", background: open ? "#f3f6f9" : "#fff",
          padding: "14px 18px", cursor: "pointer", fontSize: 15, fontWeight: 700, color: C.ink,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span>Build Final PDF (merge exhibits)</span>
          <span style={{ color: C.accent }}>{open ? "\u2212" : "+"}</span>
        </button>

        {open && (
          <div style={{ padding: "16px 18px", borderTop: `1px solid ${C.line}` }}>
            <p style={{ margin: "0 0 14px", fontSize: 13, color: C.sub, lineHeight: 1.5 }}>
              First download the Word document above and make any edits. Then upload your
              finalized Word file here, attach the exhibit PDFs, choose which exhibits to
              include, and build a single merged PDF for delivery.
            </p>

            {fileRow("Finalized Word manual (.docx)", editedManual ? [editedManual] : [],
              (arr) => setEditedManual(arr[0] || null), ".docx,.doc")}

            <div style={{ borderTop: `1px solid ${C.line}`, margin: "14px 0", paddingTop: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase",
                letterSpacing: ".06em", color: C.accent, marginBottom: 8 }}>
                Exhibits
              </div>

              <div style={{ fontSize: 13, color: C.sub, marginBottom: 12, lineHeight: 1.5 }}>
                The following exhibits are inserted automatically from the server copies:
                <ul style={{ margin: "6px 0 0 18px" }}>
                  {has("exhibit-8703") && <li>IRS Form 8703</li>}
                  {has("exhibit-2004-39") && <li>Revenue Procedure 2004-39</li>}
                  {has("exhibit-2017-13") && <li>Revenue Procedure 2017-13</li>}
                  {has("exhibit-irs-guide") && <li>IRS Tax Compliance Guide</li>}
                </ul>
                {(() => {
                  const missing = [];
                  if (has("exhibit-8703") && refsAvailable.form_8703 === false) missing.push("Form 8703");
                  if (has("exhibit-2004-39") && refsAvailable["rev_proc_2004-39"] === false) missing.push("Rev. Proc. 2004-39");
                  if (has("exhibit-2017-13") && refsAvailable["rev_proc_2017-13"] === false) missing.push("Rev. Proc. 2017-13");
                  if (has("exhibit-irs-guide") && refsAvailable.irs_tax_compliance_guide === false) missing.push("IRS Tax Compliance Guide");
                  return missing.length ? (
                    <div style={{ color: "#b23b3b", marginTop: 8, fontSize: 12 }}>
                      Not yet on server (will be skipped): {missing.join(", ")}
                    </div>
                  ) : null;
                })()}
              </div>

              {has("exhibit-management-contracts") && (
                <>
                  {checkRow("tax_certificates", "Tax Certificates / management contracts (upload below)")}
                  {exhibits.tax_certificates && fileRow("Tax Certificate PDF(s)", certFiles, setCertFiles, ".pdf")}
                </>
              )}
            </div>

            {error && (
              <div style={{ background: "#fdeaea", border: "1px solid #e3b5b5", color: "#a33",
                borderRadius: 6, padding: "9px 12px", fontSize: 13, marginBottom: 12 }}>
                {error}
              </div>
            )}

            <button className="btn btn-primary" onClick={buildPdf} disabled={busy}>
              {busy ? "Building PDF\u2026" : "Build Final PDF"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ManualView({ C, form, sections, fmtDate, manualRef, onBack, onDownloadWord }) {
  const name = form.borrowerName || "[Borrower Name]";
  const sig = significanceParagraphs(form.borrowerName);

  const H = (n) => sections.find((s) => s.id === n);

  return (
    <div style={{ background: "#e9ecf1", height: "calc(100vh - 60px)", overflowY: "auto", padding: "0 0 60px" }}>
      {/* toolbar */}
      <div className="no-print ui" style={{
        position: "sticky", top: 0, zIndex: 10, background: "#fff",
        borderBottom: `1px solid ${C.line}`, padding: "12px 22px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <button className="btn btn-ghost" onClick={onBack}>&larr; Edit details</button>
        <div style={{ fontSize: 13, color: C.sub }}>Preview. Download as Word to edit and deliver.</div>
        <button className="btn btn-primary" onClick={onDownloadWord}>Download Word Document</button>
      </div>

      <MergePanel C={C} form={form} sections={sections} />

      <div ref={manualRef} className="manual" style={{
        maxWidth: 816, margin: "26px auto", background: "#fff",
        boxShadow: "0 2px 20px rgba(0,0,0,.12)", padding: "0",
      }}>
        {/* ---------- COVER PAGE ---------- */}
        <CoverPage C={C} name={name} form={form} fmtDate={fmtDate} sig={sig} />

        {/* ---------- PROJECT COMPLIANCE PROFILE ---------- */}
        {form.bonds.map((b, i) => (
          <div key={`profile-${i}`} className="page-break" style={pageStyle}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.accent, letterSpacing: ".08em", marginBottom: 28 }}>
              PROJECT COMPLIANCE PROFILE
            </div>
            <h2 style={{ fontFamily: "Georgia, serif", fontSize: 22, margin: "0 0 28px", color: C.ink, lineHeight: 1.25 }}>
              {b.projectName || b.issueName || `Project ${i + 1}`}
            </h2>
            <InfoTable C={C} rows={[
              ["Project / address", [b.projectName, b.projectLocation].filter(Boolean).join(", ") || "____________________"],
              ["Bond issue", b.issueName || "____________________"],
              ["Issue date", b.issueDate ? fmtDate(b.issueDate) : "____________________"],
              ["Issuer / conduit borrower / owner", [b.issuer, b.conduitBorrowerOwner].filter(Boolean).join(" / ") || "____________________"],
              ["Form 8703 operator", b.form8703Operator || "____________________"],
              ["Current property manager", b.propertyManager || "____________________"],
              ["Section 142(d) election", b.setAsideElection || "____________________"],
              ["Total residential units", b.units || "____________________"],
              ["Conservative low-income-unit target", b.lowIncomeTarget || "____________________"],
              ["Qualified project period began", b.qppBegan ? fmtDate(b.qppBegan) : "____________________"],
              ["Earliest possible end of qualified project period", b.qppEarliestEnd ? fmtDate(b.qppEarliestEnd) : "____________________"],
              ["Other outstanding tax-exempt bonds for project", b.otherBondsForProject || "____________________"],
              ["Section 8 / HUD assistance", b.section8Hud || "____________________"],
              ["LIHTC status / BINs", b.lihtcStatus || "____________________"],
              ["Latest Form 8703 filing date and proof of filing", b.latest8703Filing || "____________________"],
              ["Tax Certificate / Regulatory Agreement location", b.taxCertRegAgreementLocation || "____________________"],
              ["Rebate status / next computation date", b.rebateStatusNextDate || "____________________"],
              ["Bond counsel / rebate consultant contacts", b.bondCounselRebateContacts || "____________________"],
            ]} />
            {b.transitionPeriodUsed === "yes" ? (
              <P>Rev. Proc. 2004-39 transition period: used. The Project first satisfied the
                elected set-aside on {b.transitionPeriodSatisfiedDate ? fmtDate(b.transitionPeriodSatisfiedDate) : "____________________"}.</P>
            ) : (
              <P>Rev. Proc. 2004-39 transition period: not used.</P>
            )}
          </div>
        ))}

        {/* ---------- TABLE OF CONTENTS ---------- */}
        <div className="page-break" style={pageStyle}>
          <SectionHead C={C}>Table of Contents</SectionHead>
          <div style={{ marginTop: 18 }}>
            {sections.map((s, i) => {
              const prev = sections[i - 1];
              const showExhibitDivider = s.isExhibit && (!prev || !prev.isExhibit);
              return (
                <div key={s.id}>
                  {showExhibitDivider && (
                    <div style={{ margin: "22px 0 14px", paddingTop: 12, borderTop: `1px solid ${C.line}`,
                      fontSize: 12, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: C.accent }}>
                      Exhibits
                    </div>
                  )}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
                      <span style={{ fontWeight: 700, color: C.accent, minWidth: 64 }}>
                        {s.isExhibit ? `Exhibit ${s.exhibitLetter}` : `${s.displayNum}.`}
                      </span>
                      <span style={{ fontWeight: 700, fontSize: 15 }}>{s.title}</span>
                    </div>
                    <p style={{ margin: "4px 0 0 74px", fontSize: 13.5, color: C.sub, lineHeight: 1.5 }}>
                      {s.toc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ---------- ADOPTION (immediately after table of contents) ---------- */}
        <div className="page-break" style={pageStyle}>
          <SectionHead C={C}>Adoption</SectionHead>
          <P>These policies and procedures were adopted by {name} as of {fmtDate(form.manualDate)}.</P>
          <div style={{ marginTop: 56 }}>
            <div style={{ borderTop: `1px solid ${C.ink}`, width: 320, paddingTop: 6, fontSize: 13 }}>
              Authorized Officer
            </div>
            <div style={{ marginTop: 36, borderTop: `1px solid ${C.ink}`, width: 320, paddingTop: 6, fontSize: 13 }}>
              Title
            </div>
          </div>
          <div style={{ marginTop: 48 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.accent, letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 8 }}>
              Annual / Periodic Review
            </div>
            <P>This Manual should be reviewed periodically. Record each review below.</P>
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8, fontSize: 13.5 }}>
              <thead>
                <tr style={{ background: "#eef1f5" }}>
                  <th style={{ textAlign: "left", padding: "6px 8px", border: `1px solid #b9c0cb`, color: C.accent, width: "40%" }}>Reviewed by</th>
                  <th style={{ textAlign: "left", padding: "6px 8px", border: `1px solid #b9c0cb`, color: C.accent, width: "35%" }}>Title</th>
                  <th style={{ textAlign: "left", padding: "6px 8px", border: `1px solid #b9c0cb`, color: C.accent, width: "25%" }}>Date of review</th>
                </tr>
              </thead>
              <tbody>
                {[0, 1, 2, 3, 4].map((r) => (
                  <tr key={r}>
                    <td style={{ padding: "14px 8px", border: `1px solid #b9c0cb` }}></td>
                    <td style={{ padding: "14px 8px", border: `1px solid #b9c0cb` }}></td>
                    <td style={{ padding: "14px 8px", border: `1px solid #b9c0cb` }}></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ---------- SECTIONS ---------- */}
        {H("responsibility") && (
          <Section C={C} label={H("responsibility").label} num={H("responsibility").displayNum} title={H("responsibility").title}>
            <P>The Borrower designates the officer identified below as its Federal Tax
              Compliance Officer. The Federal Tax Compliance Officer is responsible for
              administering these policies, maintaining the compliance file, coordinating with
              the Project operator and property manager, and causing material matters to be
              referred to bond counsel.</P>
            <InfoTable C={C} rows={[
              ["Federal Tax Compliance Officer", form.ftcoName || "____________________"],
              ["Title", form.ftcoTitle || "____________________"],
              ["Email / Telephone", form.ftcoContact || "____________________"],
            ]} />
            <P>The Federal Tax Compliance Officer may delegate particular administrative tasks
              to employees, affiliates, property managers, accountants, rebate consultants, or
              other service providers. Any delegation must be documented in writing and must
              identify the delegated responsibilities, reporting deadlines, and required
              records. Delegation does not relieve the Borrower of responsibility for compliance
              with its bond documents or applicable federal tax requirements.</P>
            <P>The person identified as the operator of a Project for Form 8703 purposes must be
              the person to whom the Issuer or owner has delegated responsibility for ensuring
              compliance with the qualified residential rental project requirements. The
              identity of the operator, the person authorized to sign Form 8703, and the
              identity of the property manager must be shown in the Project Compliance
              Profile.</P>
            <P>The Federal Tax Compliance Officer shall conduct, or cause to be conducted, an
              annual compliance review for each Project. The annual review must include:</P>
            <ul style={{ margin: "0 0 12px 18px", fontSize: 14, lineHeight: 1.7 }}>
              <li>review of the Project's Section 142(d) unit counts, tenant-income
                certifications, available-unit information, vacancy information, and any
                applicable next-available-unit analysis;</li>
              <li>confirmation that the Project remains residential rental property available to
                the general public on a non-transient basis;</li>
              <li>review of new or renewed management agreements, service contracts, leases,
                master leases, commercial-use arrangements, and other special legal entitlements
                affecting the Project;</li>
              <li>confirmation of continued ownership by a governmental unit or a Section
                501(c)(3) organization;</li>
              <li>review of any sale, transfer, refinancing, casualty, condemnation, insurance
                recovery, substantial rehabilitation, or change in Project operations;</li>
              <li>preparation, review, execution, filing, and retention of Form 8703 and
                evidence of timely filing; and</li>
              <li>review of arbitrage, rebate, investment, and record-retention obligations
                under the applicable Tax Certificate.</li>
            </ul>
            <P>The property manager shall deliver an annual written Section 142(d) compliance
              report to the Federal Tax Compliance Officer no later than January 31 of each
              year. The report must provide sufficient information to complete and support Form
              8703 for the preceding calendar year and must identify any actual or potential
              failure, including over-income tenants, available comparable or smaller units
              leased to nonqualifying tenants, vacancies, units held offline for renovation,
              short-term occupancy, master leases, and deviations from ordinary tenant-screening
              procedures.</P>
            <P>The Federal Tax Compliance Officer shall cause Form 8703 to be reviewed and filed
              sufficiently in advance of the March 31 filing deadline and shall retain a
              completed copy of the filed form, proof of filing, and all supporting records.</P>
            <P>Before entering into, approving, or materially modifying any of the following,
              the Federal Tax Compliance Officer shall consult bond counsel:</P>
            <ul style={{ margin: "0 0 12px 18px", fontSize: 14, lineHeight: 1.7 }}>
              <li>sale, transfer, merger, change of control, or other disposition of any Project
                or material portion thereof;</li>
              <li>change in ownership, organizational form, or federal tax-exempt status of the
                Borrower or Project owner;</li>
              <li>conversion to condominium, cooperative, transient, hotel, student-housing,
                assisted-living, or other nontraditional residential use;</li>
              <li>any new property-management agreement, master lease, corporate-housing
                arrangement, commercial lease, concession, rooftop or telecommunications
                arrangement, solar arrangement, laundry arrangement, advertising arrangement, or
                revenue-sharing arrangement;</li>
              <li>any restriction or preference affecting tenant eligibility, including a
                veterans, employer, university, social-organization, or supportive-housing
                restriction;</li>
              <li>any material change in Section 8 or other housing-assistance arrangements;</li>
              <li>casualty, condemnation, insurance proceeds, sale proceeds, refinancing,
                defeasance, redemption, or modification of the Bonds or loan documents; or</li>
              <li>discovery of any potential violation of Section 142(d), Section 145, Section
                148, or the Tax Certificate.</li>
            </ul>
            <P>Any employee, property manager, or other service provider that identifies a
              potential compliance failure shall notify the Federal Tax Compliance Officer
              promptly, and in no event later than five business days after discovery. The
              Borrower shall preserve all relevant records, take reasonable steps to prevent
              additional noncompliance, and consult bond counsel before attempting corrective
              action.</P>
          </Section>
        )}

        {H("bonds") && (
          <Section C={C} label={H("bonds").label} num={H("bonds").displayNum} title={H("bonds").title}>
            <P>The following qualified 501(c)(3) bond issues are outstanding:</P>
            {form.bonds.map((b, i) => (
              <div key={i} style={{ marginBottom: 18 }}>
                <div style={{ fontWeight: 700, fontSize: 14.5, marginBottom: 6 }}>
                  {b.issueName || `Bond Issue ${i + 1}`}
                </div>
                <InfoTable C={C} rows={[
                  ["Issuer", b.issuer || "\u2014"],
                  ["Date of Issue", b.issueDate ? fmtDate(b.issueDate) : "\u2014"],
                  ["Principal Amount", b.principalAmount ? fmtCurrency(b.principalAmount) : "\u2014"],
                  ["Final Maturity", b.finalMaturity ? fmtDate(b.finalMaturity) : "\u2014"],
                  ["Financed Project", b.projectName || "\u2014"],
                  ["Location", b.projectLocation || "\u2014"],
                  ["Residential Units", b.units || "\u2014"],
                ]} />
              </div>
            ))}
          </Section>
        )}

        {H("certs") && (
          <Section C={C} label={H("certs").label} num={H("certs").displayNum} title={H("certs").title}>
            <P>The executed tax certificate for each bond issue is attached to this manual.
              Each tax certificate sets forth the Borrower's representations and covenants
              concerning the federal tax requirements applicable to the related bonds,
              including the use of proceeds, the use of the financed facilities, arbitrage
              and rebate, and the residential rental set-aside.</P>
            <Placeholder C={C} text="Attach the executed tax certificate for each issue behind this page." />
            <ul style={{ margin: "8px 0 0 18px", fontSize: 14, lineHeight: 1.7 }}>
              {form.bonds.map((b, i) => (
                <li key={i}>{b.issueName || `Bond Issue ${i + 1}`}
                  {b.issueDate ? ` (${fmtDate(b.issueDate)})` : ""}</li>
              ))}
            </ul>
          </Section>
        )}

        {H("setaside-explanation") && (
          <Section C={C} label={H("setaside-explanation").label} num={H("setaside-explanation").displayNum} title={H("setaside-explanation").title}>
            <P>Because all or a portion of the net proceeds of the Bonds were used to provide
              residential rental property for family units that was not newly constructed, the
              Bonds rely upon the exception in Section 145(d)(2)(B) of the Code for qualified
              residential rental projects described in Section 142(d) of the Code. Each Project
              must therefore satisfy a continuing low-income occupancy set-aside throughout its
              qualified project period.</P>
            <P><strong>The set-aside.</strong> The Issuer elected, at the time of issuance, one
              of the following set-aside requirements for each Project. This Manual does not
              amend the election; the applicable election for each Project is identified in the
              Project Compliance Profile.</P>
            <ul style={{ margin: "0 0 12px 18px", fontSize: 14, lineHeight: 1.7 }}>
              <li>at least 20% of the residential units occupied by individuals whose income
                is 50% or less of area median gross income (the "20-50 test"); or</li>
              <li>at least 40% of the residential units occupied by individuals whose income
                is 60% or less of area median gross income (the "40-60 test").</li>
            </ul>
            <P><strong>Qualified project period.</strong> For an acquired, already-operating
              Project in which at least 10% of the units were occupied before the issue date,
              the qualified project period generally begins on the issue date. The set-aside
              requirements apply to the total number of available units beginning on the later
              of the first day on which at least 10% of all residential units are occupied and
              the issue date. The qualified project period ends on the latest of (i) the date
              that is 15 years after the date on which at least 50% of the residential units
              were first occupied, (ii) the first day on which no tax-exempt bond issued with
              respect to the Project is outstanding, or (iii) the date on which any assistance
              provided with respect to the Project under Section 8 of the United States Housing
              Act of 1937 terminates.</P>
            <P><strong>Available-unit rule.</strong> If the income of an occupant of a
              low-income unit increases above the applicable limit, the project may continue to
              treat that unit as a low-income unit only so long as the next available unit of
              comparable or smaller size in the project is rented to a qualifying tenant. The
              Federal Tax Compliance Officer should confirm that the property manager applies
              this rule on a continuing basis.</P>
            <P><strong>Annual certification.</strong> The operator of each Project must
              certify annually to the Secretary, on IRS Form 8703, that the Project met the
              applicable set-aside requirement. Revenue Procedure 2004-39 sets forth the
              procedures for determining whether a Project satisfies the Section 142(d)
              set-aside requirements during the qualified project period. The certification is
              described in the following section.</P>
            <P><strong>Transition period for acquired projects.</strong> Under Revenue
              Procedure 2004-39, a special rule applies to the acquisition of an existing
              residential rental project. For a period of 12 months beginning on the issue date
              of the first bonds (the "transition period"), a failure to satisfy the set-aside
              requirement will not cause the Project to fail to be a qualified residential
              rental project. This special rule does not apply if more than 90% of the
              residential units in the Project are not available units at any time within the
              60-day period after the later of the date the Project is acquired or the issue
              date of the first bonds. Whether the transition period was used for a Project, and
              the date on which the Project first satisfied the elected set-aside, are
              documented in the Project Compliance Profile.</P>

            {form.bonds.some((b) => b.detailLevel === "detailed") && (
              <div style={{ marginTop: 8 }}>
                {form.bonds.map((b, i) =>
                  b.detailLevel !== "detailed" ? null : (
                    <div key={i} style={{ marginTop: 16 }}>
                      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>
                        Additional Requirements: {b.projectName || b.issueName || `Project ${i + 1}`}
                      </div>
                      <P>The set-aside election was made by the Issuer at the time of issuance
                        and generally may not be revoked. Income limits are adjusted for family
                        size, and the income of all occupants of a unit is counted. The Section
                        142(d) set-aside is an income test, not a rent test (unlike the
                        low-income housing tax credit under Section 42). Where the Project also
                        carries Section 42 credits, care should be taken not to conflate the two
                        regimes, because the income limits, rent restrictions, and compliance
                        periods differ.</P>
                      <P><strong>Deep-rent-skewed alternative.</strong> The Project may qualify
                        under the deep-rent-skewed alternative of Section 142(d)(4)(B), under
                        which 15% or more of the low-income units are occupied by tenants whose
                        income is 40% or less of area median gross income and the gross rent on
                        each low-income unit does not exceed one-half the gross rent on
                        comparable market-rate units. A Project relying on this alternative is
                        subject to additional rent and occupancy constraints and should be
                        flagged separately in the annual review.</P>
                      <P><strong>Income recertification.</strong> Tenant income is determined
                        and certified at the commencement of occupancy. Under Section 142(d)(3),
                        an owner need not annually recertify tenant income for a year in which no
                        residential unit in the Project is occupied by a new resident whose
                        income exceeds the applicable income limit. The operator should retain
                        the supporting income documentation for each certification. The relevant
                        authorities are Section 142(d)(3), Treasury Regulations Section
                        1.103-8(b), and Revenue Procedure 2004-39.</P>
                    </div>
                  )
                )}
              </div>
            )}
          </Section>
        )}

        {H("form8703-explanation") && (
          <Section C={C} label={H("form8703-explanation").label} num={H("form8703-explanation").displayNum} title={H("form8703-explanation").title}>
            <P>IRS Form 8703, <em>Annual Certification of a Residential Rental Project</em>,
              is the form on which the operator of a qualified residential rental project
              certifies, for each calendar year in the qualified project period, that the
              project complied with the applicable low-income set-aside.</P>
            <P><strong>Who must file.</strong> The operator of the Project must file a separate
              Form 8703 for each Project. For this purpose, the operator is the person to whom
              the Issuer or owner has delegated responsibility for ensuring compliance with the
              qualified residential rental project requirements. A property manager is not
              automatically the operator merely because it manages the building. The operator
              for each Project is identified in the Project Compliance Profile.</P>
            <P><strong>When to file.</strong> Form 8703 must be filed annually, no later than
              March 31 following the close of each calendar year in the qualified project
              period. A Form 8703 may remain due after the Bonds are redeemed if the qualified
              project period has not ended.</P>
            <P><strong>What it requires.</strong> The form identifies the Project and the
              issue that financed it, states the set-aside election in effect, and reports
              the number of units and the number occupied by qualifying low-income tenants as
              of the certification date.</P>
            <P><strong>Effect of a filing failure.</strong> A failure to file Form 8703 does
              not, by itself, affect the tax-exempt status of interest on the Bonds, but it can
              trigger the penalty under Section 6652(j) and must be corrected promptly.</P>
          </Section>
        )}

        {H("form8703-copies") && (
          <Section C={C} label={H("form8703-copies").label} num={H("form8703-copies").displayNum} title={H("form8703-copies").title}>
            <P>A copy of the most recently filed Form 8703 for each financed project is
              attached behind this page and retained as part of the compliance record.</P>
            <Placeholder C={C} text="Attach the filed Form 8703 for each project listed below." />
            <ul style={{ margin: "8px 0 0 18px", fontSize: 14, lineHeight: 1.7 }}>
              {form.bonds.map((b, i) => (
                <li key={i}>{b.projectName || `Project ${i + 1}`}
                  {b.projectLocation ? ` \u2014 ${b.projectLocation}` : ""}
                  {b.setAsideElection ? ` (${b.setAsideElection} election)` : ""}</li>
              ))}
            </ul>
          </Section>
        )}

        {H("use-restrictions") && (
          <Section C={C} label={H("use-restrictions").label} num={H("use-restrictions").displayNum} title={H("use-restrictions").title}>
            <P>For the life of the Bonds, each Project must continuously satisfy the
              operational requirements of a qualified residential rental project:</P>
            <ul style={{ margin: "0 0 12px 18px", fontSize: 14, lineHeight: 1.7 }}>
              <li>The Project must be residential rental property used on a continuous,
                non-transient basis (subject to the limited exceptions for certain
                single-room-occupancy and transitional housing).</li>
              <li>All residential units must be available for use by the general public and
                may not be used on a transient basis.</li>
              <li>The Project must remain a discrete edifice or set of facilities containing
                similarly constructed units.</li>
              <li>The applicable low-income set-aside must be maintained throughout the
                qualified project period, including compliance with the available-unit rule.</li>
            </ul>
            <P>A Project does not fail to be residential rental property merely because part of
              the building or structure is used for purposes other than residential rental.
              However, mixed-use and commercial space, leasing of common areas, short-term
              occupancy, corporate housing, master leases, and similar arrangements can affect
              qualification and must be pre-cleared with bond counsel as provided in Section 1
              before they are entered into or modified.</P>

            {form.bonds.some((b) => b.detailLevel === "detailed") && (
              <>
                <P><strong>Continuous-rental and facilities requirements.</strong> Under
                  Treasury Regulations Section 1.103-8(b) and Section 142(d), the Project must
                  consist of a building or structure containing similarly constructed units,
                  together with functionally related and subordinate facilities (such as
                  parking, common areas, and facilities for use by tenants). Units must be
                  used on other than a transient basis. Facilities not functionally related and
                  subordinate, or units made available to the general public on a transient
                  basis (for example, hotel or motel use), can cause the Project to fail to
                  qualify.</P>
                <P><strong>Single-room occupancy and certain exceptions.</strong> Single-room
                  occupancy housing and certain residential rental projects providing
                  supportive services may qualify notwithstanding the general non-transient
                  requirement, subject to the applicable conditions. Whether a particular
                  Project qualifies under one of these exceptions should be confirmed with bond
                  counsel.</P>
              </>
            )}

            <P>Any proposed change in the use of a Project, sale or transfer of a Project, or
              change in the property manager should be discussed with bond counsel before it
              occurs, because such changes can affect the tax-exempt status of the Bonds.</P>
          </Section>
        )}

        {H("pbu") && (
          <Section C={C} label={H("pbu").label} num={H("pbu").displayNum} title={H("pbu").title}>
            <P>Interest on the Bonds is excluded from gross income only if the financed
              facilities are not used for a private business use beyond the limits permitted
              by Sections 141 and 145 of the Code. Use by the 501(c)(3) Borrower in an
              unrelated trade or business, and use by other nongovernmental persons other than
              as a member of the general public, are treated as private business use and are
              subject to limitation.</P>
            <P><strong>What counts as private business use.</strong> Private business use
              generally arises when a nongovernmental person, or a 501(c)(3) organization
              acting outside the scope of its exempt purpose, uses bond-financed property other
              than as a member of the general public. Common sources include leases, management
              or service contracts, research agreements, naming rights, and other special legal
              entitlements to use the financed space. For these purposes, a Section 501(c)(3)
              organization using the property in furtherance of its exempt purpose is generally
              treated like a governmental unit.</P>
            <P><strong>Management and service contracts.</strong> A management or service
              contract with respect to bond-financed property can give rise to private business
              use unless it satisfies the safe harbor conditions of Revenue Procedure 2017-13.
              In general, those conditions require that the compensation be reasonable and not
              based on a share of net profits from the operation of the facility, that the term
              not exceed the limits in the revenue procedure, and that the qualified user
              exercise a significant degree of control over the use of the property and bear
              the risk of loss. A copy of Revenue Procedure 2017-13 is included as an exhibit
              to this Manual.</P>
            <P><strong>Monitoring and escalation.</strong> The Federal Tax Compliance Officer
              should review each new or renewed lease, management contract, service contract,
              or research agreement affecting the financed facilities before it is executed,
              and should consult bond counsel where any arrangement may give rise to private
              business use. Any unusual advance, prepayment, or lending arrangement involving
              bond proceeds or the financed facilities should be escalated to bond counsel
              before it is entered into.</P>
          </Section>
        )}

        {H("arbitrage") && (
          <Section C={C} label={H("arbitrage").label} num={H("arbitrage").displayNum} title={H("arbitrage").title}>
            <P>The bonds are subject to the arbitrage and rebate requirements of Section 148
              of the Code. In general, the Borrower may not invest bond proceeds at a yield
              materially higher than the yield on the bonds, except during permitted
              temporary periods, and must pay to the United States any arbitrage profit that
              is required to be rebated.</P>
            <ul style={{ margin: "0 0 12px 18px", fontSize: 14, lineHeight: 1.7 }}>
              <li>Track the investment and expenditure of all gross proceeds, including any
                reserve funds.</li>
              <li>Determine whether a spending exception to rebate applies and, if so,
                document satisfaction of the applicable spending milestones.</li>
              <li>Compute and pay any required rebate on each installment computation date
                (generally every five years) and on the final redemption of the bonds.</li>
            </ul>
            <P>The Borrower should engage a qualified rebate analyst as needed and retain the
              resulting computations as described in the recordkeeping section.</P>
          </Section>
        )}

        {H("records") && (
          <Section C={C} label={H("records").label} num={H("records").displayNum} title={H("records").title}>
            <P>The Borrower must maintain records sufficient to establish continuing
              compliance with the federal tax requirements. Records must be retained for the
              life of the bond issue, plus the life of any bond issue that refunds it, plus
              three years.</P>
            <P>Records to be retained include, at a minimum:</P>
            <ul style={{ margin: "0 0 12px 18px", fontSize: 14, lineHeight: 1.7 }}>
              <li>the transcript of proceedings for each bond issue, including the tax
                certificate;</li>
              <li>records of the expenditure of bond proceeds;</li>
              <li>tenant income certifications and the data supporting each annual set-aside
                determination;</li>
              <li>each filed Form 8703 and proof of filing; and</li>
              <li>investment records and any rebate computations.</li>
            </ul>
          </Section>
        )}

        {H("remediation") && (
          <Section C={C} label={H("remediation").label} num={H("remediation").displayNum} title={H("remediation").title}>
            <P>A potential Section 142(d) failure requires immediate analysis. The Borrower may
              not assume that later restoration of the elected set-aside, a generic remedial
              action, or a voluntary filing will retroactively cure the failure.</P>
            <P>If the Federal Tax Compliance Officer identifies a potential failure to comply
              with the federal tax requirements, the Borrower should promptly consult bond
              counsel and preserve all relevant records. The remedial-action regime under
              Treasury Regulations Section 1.141-12 does not generally apply to a Section 145(d)
              or Section 142(d) failure; Section 142(d) noncompliance is governed by its own
              reasonable-period correction rule and the acquisition-transition rule of Revenue
              Procedure 2004-39. A sale or other loss of Section 501(c)(3) ownership may, by
              contrast, have remedies under Section 1.141-12, but those remedies are highly
              time-sensitive and issuer-driven and must be pursued immediately with bond
              counsel.</P>
            <P>Where available, a violation may also be resolved through the IRS Tax-Exempt
              Bonds Voluntary Closing Agreement Program (VCAP). Prompt identification and
              correction generally produces a more favorable resolution than a violation
              discovered on examination.</P>
          </Section>
        )}

        {H("exhibit-8703") && (
          <Section C={C} label={H("exhibit-8703").label} num={H("exhibit-8703").displayNum} title={H("exhibit-8703").title}>
            <P>A blank copy of IRS Form 8703, Annual Certification of a Residential Rental
              Project, is attached behind this page for use in making the annual set-aside
              certification.</P>
          </Section>
        )}

        {H("exhibit-2004-39") && (
          <Section C={C} label={H("exhibit-2004-39").label} num={H("exhibit-2004-39").displayNum} title={H("exhibit-2004-39").title}>
            <P>A copy of Revenue Procedure 2004-39 is attached behind this page. It sets forth
              the procedures for determining whether a residential rental project satisfies the
              Section 142(d) set-aside requirements during the qualified project period,
              including the transition period applicable to acquired projects.</P>
          </Section>
        )}

        {H("exhibit-2017-13") && (
          <Section C={C} label={H("exhibit-2017-13").label} num={H("exhibit-2017-13").displayNum} title={H("exhibit-2017-13").title}>
            <P>A copy of Revenue Procedure 2017-13 is attached behind this page. It sets forth
              the safe harbor conditions under which a management or service contract does not
              result in private business use of bond-financed property.</P>
          </Section>
        )}

        {H("exhibit-irs-guide") && (
          <Section C={C} label={H("exhibit-irs-guide").label} num={H("exhibit-irs-guide").displayNum} title={H("exhibit-irs-guide").title}>
            <P>A copy of the IRS Tax Compliance Guide is attached behind this page as a general
              reference on post-issuance compliance for tax-exempt bonds.</P>
          </Section>
        )}

        {H("exhibit-management-contracts") && (
          <Section C={C} label={H("exhibit-management-contracts").label} num={H("exhibit-management-contracts").displayNum} title={H("exhibit-management-contracts").title}>
            <P>The Borrower's executed management and service contracts affecting the
              bond-financed facilities are attached behind this page. Each such contract should
              be reviewed against the private business use rules and the safe harbor conditions
              of Revenue Procedure 2017-13.</P>
          </Section>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Manual sub-components
// ---------------------------------------------------------------------------
const pageStyle = {
  padding: "0.85in 0.9in",
  minHeight: "9.5in",
};

function CoverPage({ C, name, form, fmtDate, sig }) {
  return (
    <div style={{ ...pageStyle, display: "flex", flexDirection: "column" }}>
      <div style={{ fontSize: 11, letterSpacing: ".22em", textTransform: "uppercase", color: C.accent, fontWeight: 700 }}>
        Federal Tax Post-Issuance Compliance Manual
      </div>
      <div style={{ borderBottom: `3px solid ${C.accent}`, marginTop: 8 }} />

      <div style={{ marginTop: 96, textAlign: "center" }}>
        <div style={{ borderTop: `1px solid ${C.line}`, width: 90, margin: "0 auto 26px" }} />
        <h1 style={{ fontFamily: "Georgia, serif", fontSize: 32, lineHeight: 1.22, margin: 0, color: C.ink, fontWeight: 700 }}>
          {name}
        </h1>
        <div style={{ fontSize: 16, marginTop: 16, color: C.sub, fontStyle: "italic", fontFamily: "Georgia, serif" }}>
          Federal Tax Post-Issuance Compliance Manual
        </div>
        <div style={{ borderTop: `1px solid ${C.line}`, width: 90, margin: "26px auto 0" }} />
        <div style={{ fontSize: 13, marginTop: 22, color: C.sub, letterSpacing: ".04em" }}>
          Date Adopted: {fmtDate(form.manualDate)}
        </div>
        {form.lastUpdated && (
          <div style={{ fontSize: 12, marginTop: 6, color: C.sub, letterSpacing: ".04em" }}>
            Last Updated: {fmtDate(form.lastUpdated)}
          </div>
        )}
      </div>

      <div style={{ marginTop: 72 }}>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".1em", color: C.accent, marginBottom: 12 }}>
          Purpose, Scope, and Significance
        </div>
        {sig.map((p, i) => (
          <p key={i} style={{ fontSize: 13.5, lineHeight: 1.62, margin: "0 0 12px", textAlign: "justify" }}>{p}</p>
        ))}
      </div>

      <div style={{ marginTop: "auto", paddingTop: 30, fontSize: 12, color: C.sub, borderTop: `1px solid ${C.line}` }}>
        Federal Tax Compliance Officer: {form.ftcoName || "____________________"}
        {form.ftcoTitle ? `, ${form.ftcoTitle}` : ""}
      </div>
    </div>
  );
}

const SectionHead = ({ C, children }) => (
  <div>
    <h2 style={{ fontFamily: "Georgia, serif", fontSize: 22, margin: 0, color: C.ink }}>{children}</h2>
    <div style={{ borderBottom: `2px solid ${C.accent}`, width: 60, marginTop: 8 }} />
  </div>
);

const Section = ({ C, label, num, title, children }) => (
  <div className="page-break" style={pageStyle}>
    <div style={{ fontSize: 12, fontWeight: 700, color: C.accent, letterSpacing: ".08em", marginBottom: 16 }}>
      {label || `SECTION ${num}`}
    </div>
    <h2 style={{ fontFamily: "Georgia, serif", fontSize: 22, margin: "0 0 10px", color: C.ink, lineHeight: 1.25 }}>{title}</h2>
    <div style={{ borderBottom: `2px solid ${C.accent}`, width: 60, margin: "0 0 20px" }} />
    {children}
  </div>
);

const P = ({ children }) => (
  <p style={{ fontSize: 14, lineHeight: 1.62, margin: "0 0 12px", textAlign: "justify" }}>{children}</p>
);

function InfoTable({ C, rows }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", margin: "6px 0 14px", fontSize: 13.5 }}>
      <tbody>
        {rows.map(([k, v], i) => (
          <tr key={i} style={{ borderBottom: `1px solid ${C.line}` }}>
            <td style={{ padding: "7px 12px 7px 0", fontWeight: 600, color: C.sub, width: "38%", verticalAlign: "top" }}>{k}</td>
            <td style={{ padding: "7px 0", verticalAlign: "top" }}>{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const Placeholder = ({ C, text }) => (
  <div style={{
    border: `1px dashed ${C.line}`, borderRadius: 6, padding: "10px 14px",
    background: "#f7f8fa", fontSize: 13, color: C.sub, fontStyle: "italic", margin: "6px 0 12px",
  }}>
    {text}
  </div>
);

export default ComplianceManualBuilder;
